import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  X,
  GripVertical,
  Phone,
  Mail,
  Building2,
  Flame,
  Zap,
  Snowflake,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { getPipeline, updateStatus, markLeadAsLost, rescoreAllLeads } from "@/lib/admin-api";
import {
  STATUS_ORDER,
  STATUS_LABELS,
  STAGE_ACCENT,
  LOSS_REASONS,
  tempBadgeClass,
  tempLabel,
} from "@/lib/lead-constants";
import { formatMtyDateShort } from "@/lib/mty-date";
import { toast } from "sonner";

function TempPill({ temp }) {
  const Icon = temp === "hot" ? Flame : temp === "warm" ? Zap : Snowflake;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${tempBadgeClass(
        temp
      )}`}
      title={`Lead ${tempLabel(temp)}`}
      data-testid={`lead-temp-${temp}`}
    >
      <Icon className="w-3 h-3" /> {tempLabel(temp)}
    </span>
  );
}

function LeadCard({ lead, onDragStart, onDragEnd, isDragging }) {
  const nav = useNavigate();
  const handleClick = () => nav(`/admin/leads/${lead.id}`);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      data-testid={`kanban-card-${lead.id}`}
      className={`group bg-white border border-neutral-200 rounded-xl p-3 shadow-sm hover:shadow-md hover:border-neutral-300 cursor-pointer transition active:cursor-grabbing ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-neutral-900 truncate">{lead.nombre}</p>
          {lead.empresa && (
            <p className="text-[11px] text-neutral-500 truncate flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {lead.empresa}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-1">
          <GripVertical className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500" />
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <TempPill temp={lead.lead_temp || "cold"} />
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">
          {lead.lead_score ?? 0}
        </span>
        {lead.tipo_espacio && (
          <span className="text-[10px] text-neutral-500 capitalize">
            {lead.tipo_espacio}
          </span>
        )}
      </div>

      <div className="text-[11px] text-neutral-500 space-y-0.5">
        {lead.telefono && (
          <p className="flex items-center gap-1 truncate">
            <Phone className="w-3 h-3" /> {lead.telefono}
          </p>
        )}
        {lead.email && (
          <p className="flex items-center gap-1 truncate">
            <Mail className="w-3 h-3" /> {lead.email}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between text-[10px] text-neutral-400 mt-2 pt-2 border-t border-neutral-100">
        <span className="truncate">{lead.responsable || "Sin asignar"}</span>
        <span>{formatMtyDateShort(lead.created_at)}</span>
      </div>

      {lead.next_followup_at && (
        <div className="mt-1.5 px-2 py-1 bg-orange-50 border border-orange-100 rounded text-[10px] text-orange-700 font-semibold flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Seguir: {formatMtyDateShort(lead.next_followup_at)}
        </div>
      )}
    </div>
  );
}

function LossReasonModal({ open, lead, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
    }
  }, [open]);

  if (!open || !lead) return null;

  const submit = async () => {
    if (!reason) {
      toast.error("Selecciona un motivo");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ reason, note });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="loss-reason-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-black">Marcar como perdido</h3>
            <p className="text-sm text-neutral-500">{lead.nombre}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="block text-xs font-semibold text-neutral-700 mb-1.5">
          Motivo de pérdida <span className="text-red-600">*</span>
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="form-input w-full mb-4"
          data-testid="loss-reason-select"
        >
          <option value="">Selecciona un motivo...</option>
          {LOSS_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <label className="block text-xs font-semibold text-neutral-700 mb-1.5">
          Nota (opcional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="form-input w-full mb-5"
          placeholder="Contexto adicional, competencia que eligieron, etc..."
          data-testid="loss-reason-note"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-neutral-700 hover:bg-neutral-100"
            data-testid="loss-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-neutral-800 text-white hover:bg-neutral-900 disabled:opacity-60"
            data-testid="loss-confirm-btn"
          >
            {submitting ? "Guardando..." : "Marcar perdido"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminKanban() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [q, setQ] = useState("");
  const [fuente, setFuente] = useState("");
  const [responsable, setResponsable] = useState("");
  const [dragging, setDragging] = useState(null); // { leadId, fromStatus }
  const [dragOver, setDragOver] = useState(null); // status
  const [lossModal, setLossModal] = useState({ open: false, lead: null });
  const pendingMoveRef = useRef(null);

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const d = await getPipeline(params || { q, fuente, responsable });
      setData(d);
    } catch {
      toast.error("Error cargando pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ q, fuente, responsable });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    fetchData({ q, fuente, responsable });
  };
  const onClear = () => {
    setQ("");
    setFuente("");
    setResponsable("");
    fetchData({});
  };

  const responsables = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    data.stages.forEach((s) =>
      s.items.forEach((l) => {
        if (l.responsable) set.add(l.responsable);
      })
    );
    return Array.from(set).sort();
  }, [data]);

  // Drag handlers
  const onDragStart = (e, lead) => {
    setDragging({ leadId: lead.id, fromStatus: lead.estatus });
    try {
      e.dataTransfer.setData("text/plain", lead.id);
      e.dataTransfer.effectAllowed = "move";
    } catch {}
  };
  const onDragEnd = () => {
    setDragging(null);
    setDragOver(null);
  };
  const onDragOverCol = (e, status) => {
    e.preventDefault();
    if (dragOver !== status) setDragOver(status);
    try {
      e.dataTransfer.dropEffect = "move";
    } catch {}
  };
  const onDragLeaveCol = (status) => {
    if (dragOver === status) setDragOver(null);
  };

  const applyOptimisticMove = (leadId, fromStatus, toStatus, extra = {}) => {
    setData((d) => {
      if (!d) return d;
      const next = {
        ...d,
        stages: d.stages.map((s) => ({ ...s, items: [...s.items] })),
        totals: { ...d.totals },
      };
      let moved = null;
      for (const s of next.stages) {
        const idx = s.items.findIndex((i) => i.id === leadId);
        if (idx >= 0) {
          moved = s.items[idx];
          s.items.splice(idx, 1);
          s.total = Math.max(0, (s.total || 0) - 1);
          next.totals[s.estatus] = Math.max(0, (next.totals[s.estatus] || 0) - 1);
          break;
        }
      }
      if (!moved) return d;
      const target = next.stages.find((s) => s.estatus === toStatus);
      if (target) {
        const updated = { ...moved, estatus: toStatus, ...extra };
        target.items = [updated, ...target.items];
        target.total = (target.total || 0) + 1;
        next.totals[toStatus] = (next.totals[toStatus] || 0) + 1;
      }
      return next;
    });
  };

  const doStatusUpdate = async (leadId, fromStatus, toStatus) => {
    applyOptimisticMove(leadId, fromStatus, toStatus);
    try {
      const res = await updateStatus(leadId, toStatus);
      // Aplicar score devuelto por el backend
      if (res && (res.lead_score !== undefined || res.lead_temp !== undefined)) {
        setData((d) => {
          if (!d) return d;
          return {
            ...d,
            stages: d.stages.map((s) => ({
              ...s,
              items: s.items.map((i) =>
                i.id === leadId
                  ? { ...i, lead_score: res.lead_score ?? i.lead_score, lead_temp: res.lead_temp ?? i.lead_temp }
                  : i
              ),
            })),
          };
        });
      }
      toast.success(`${STATUS_LABELS[toStatus]}`);
    } catch (err) {
      toast.error("Error moviendo el lead. Reintenta.");
      // Revertir
      applyOptimisticMove(leadId, toStatus, fromStatus);
    }
  };

  const onDrop = (e, toStatus) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging) return;
    const { leadId, fromStatus } = dragging;
    if (!leadId || fromStatus === toStatus) {
      setDragging(null);
      return;
    }

    // Si se mueve a "perdido" → abrir modal para pedir motivo estructurado
    if (toStatus === "perdido") {
      const lead = data?.stages
        .flatMap((s) => s.items)
        .find((i) => i.id === leadId);
      if (lead) {
        pendingMoveRef.current = { leadId, fromStatus };
        setLossModal({ open: true, lead });
      }
      setDragging(null);
      return;
    }

    doStatusUpdate(leadId, fromStatus, toStatus);
    setDragging(null);
  };

  const confirmLoss = async ({ reason, note }) => {
    const pending = pendingMoveRef.current;
    if (!pending) return;
    const { leadId, fromStatus } = pending;
    applyOptimisticMove(leadId, fromStatus, "perdido", { loss_reason: reason });
    try {
      const res = await markLeadAsLost(leadId, { reason, note });
      if (res && (res.lead_score !== undefined || res.lead_temp !== undefined)) {
        setData((d) => {
          if (!d) return d;
          return {
            ...d,
            stages: d.stages.map((s) => ({
              ...s,
              items: s.items.map((i) =>
                i.id === leadId
                  ? { ...i, lead_score: res.lead_score ?? i.lead_score, lead_temp: res.lead_temp ?? i.lead_temp }
                  : i
              ),
            })),
          };
        });
      }
      toast.success("Lead marcado como perdido");
    } catch {
      toast.error("Error marcando como perdido");
      applyOptimisticMove(leadId, "perdido", fromStatus);
    } finally {
      pendingMoveRef.current = null;
    }
  };

  const closeLossModal = () => {
    // Si el usuario canceló, revertimos el dragging (nada que hacer porque no aplicamos optimistic move aún)
    setLossModal({ open: false, lead: null });
    pendingMoveRef.current = null;
  };

  const handleRescore = async () => {
    if (!window.confirm("Recalcular score para todos los leads. ¿Continuar?")) return;
    setRescoring(true);
    try {
      const r = await rescoreAllLeads();
      toast.success(`${r.updated} leads re-evaluados`);
      fetchData({ q, fuente, responsable });
    } catch {
      toast.error("Error recalculando");
    } finally {
      setRescoring(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px]" data-testid="admin-kanban-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black">Pipeline</h1>
          <p className="text-neutral-500 text-sm mt-1">
            {data ? `${data.grand_total} leads` : "Cargando..."} · Arrastra las tarjetas para cambiar
            de etapa
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRescore}
            disabled={rescoring}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
            data-testid="rescore-all-btn"
            title="Recalcular score de todos los leads"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${rescoring ? "animate-spin" : ""}`} />{" "}
            {rescoring ? "Recalculando..." : "Recalcular scores"}
          </button>
          <Link
            to="/admin/leads"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            Ver tabla
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-3 mb-4 flex flex-col md:flex-row gap-2 md:items-center">
        <form onSubmit={onSubmitSearch} className="flex-1 relative">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email, empresa, teléfono..."
            className="form-input pl-9 w-full"
            data-testid="kanban-search-input"
          />
        </form>
        <div className="flex gap-2 flex-wrap">
          <select
            value={fuente}
            onChange={(e) => {
              setFuente(e.target.value);
              fetchData({ q, fuente: e.target.value, responsable });
            }}
            className="form-input text-sm"
            data-testid="kanban-filter-fuente"
          >
            <option value="">Todas fuentes</option>
            <option value="cotizacion">Cotización</option>
            <option value="catalogo">Catálogo</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="directo">Directo</option>
          </select>
          <select
            value={responsable}
            onChange={(e) => {
              setResponsable(e.target.value);
              fetchData({ q, fuente, responsable: e.target.value });
            }}
            className="form-input text-sm"
            data-testid="kanban-filter-responsable"
          >
            <option value="">Todos responsables</option>
            {responsables.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {(q || fuente || responsable) && (
            <button
              onClick={onClear}
              className="px-3 py-2 text-neutral-500 hover:text-neutral-900 text-sm"
              aria-label="Limpiar filtros"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Kanban board */}
      <div
        className="flex gap-3 overflow-x-auto pb-4"
        style={{ scrollbarGutter: "stable" }}
        data-testid="kanban-board"
      >
        {loading && !data ? (
          <div className="text-neutral-400 p-8">Cargando pipeline...</div>
        ) : (
          (data?.stages || []).map((stage) => {
            const accent = STAGE_ACCENT[stage.estatus] || "border-t-neutral-300 bg-neutral-50/60";
            const isOver = dragOver === stage.estatus;
            return (
              <div
                key={stage.estatus}
                onDragOver={(e) => onDragOverCol(e, stage.estatus)}
                onDragLeave={() => onDragLeaveCol(stage.estatus)}
                onDrop={(e) => onDrop(e, stage.estatus)}
                data-testid={`kanban-col-${stage.estatus}`}
                className={`flex-shrink-0 w-[280px] rounded-2xl border-t-4 ${accent} border border-neutral-200 flex flex-col ${
                  isOver ? "ring-2 ring-red-500 ring-offset-2" : ""
                }`}
              >
                <div className="px-3 py-2.5 flex items-center justify-between border-b border-neutral-200 bg-white/70 rounded-t-xl">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-bold text-sm text-neutral-900 truncate">
                      {STATUS_LABELS[stage.estatus] || stage.estatus}
                    </h3>
                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-neutral-900 text-white">
                      {stage.total}
                    </span>
                  </div>
                </div>
                <div className="flex-1 p-2 space-y-2 min-h-[400px] max-h-[calc(100vh-260px)] overflow-y-auto">
                  {stage.items.length === 0 ? (
                    <div className="text-xs text-neutral-400 text-center py-6">
                      {isOver ? "Suelta aquí" : "Sin leads"}
                    </div>
                  ) : (
                    stage.items.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        isDragging={dragging?.leadId === lead.id}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <LossReasonModal
        open={lossModal.open}
        lead={lossModal.lead}
        onClose={closeLossModal}
        onConfirm={confirmLoss}
      />
    </div>
  );
}
